import { MonitorView } from "@/services/backend/qpm/monitors/types/monitor-view";
import {
  useQpmCatalogMonitorListQuery,
  useQpmMonitorGetQuery,
} from "@/services/backend/qpm/monitors/api/monitor-api";
import { MonitorDetailsComponent } from "@/routes/base-data/processes/qpm/monitors/[monitorId]/_components/monitor-details";
import { MonitorNameComponent } from "@/routes/base-data/processes/qpm/monitors/[monitorId]/_components/monitor-name";
import { MonitorSupplierComponent } from "@/routes/base-data/processes/qpm/monitors/[monitorId]/_components/monitor-supplier";
import { MonitorIoTSpecComponent } from "@/routes/base-data/processes/qpm/monitors/[monitorId]/_components/monitor-iot-spec";
import { MonitorCapabilitiesComponent } from "@/routes/base-data/processes/qpm/monitors/[monitorId]/_components/monitor-caps";
import { MonitorBasePriceComponent } from "@/routes/base-data/processes/qpm/monitors/[monitorId]/_components/monitor-baseprice";
import { MonitorImageComponent } from "@/routes/base-data/processes/qpm/monitors/[monitorId]/_components/monitor-image";
import { MonitorVisitsComponent } from "@/routes/base-data/processes/qpm/monitors/[monitorId]/_components/monitor-visits";
import { MonitorPestsComponent } from "@/routes/base-data/processes/qpm/monitors/[monitorId]/_components/monitor-pests";
import { MonitorConsumablesComponent } from "@/routes/base-data/processes/qpm/monitors/[monitorId]/_components/monitor-consumables";
import { MonitorTimesComponent } from "@/routes/base-data/processes/qpm/monitors/[monitorId]/_components/monitor-times";
import { MonitorTypeUtil } from "@/services/backend/qpm/lib/monitor-type-util";
import { MonitorType } from "@/services/backend/qpm/types/enums";
import {
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import t from "@/lang/lang";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { ChevronLeft, ChevronRight, Loader } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { Button } from "@/shared/components/ui/button";
import { useMemo } from "react";
import { useNavigate, useParams } from "react-router";
import { MonitorRentingComponent } from "@/routes/base-data/processes/qpm/monitors/[monitorId]/_components/monitor-renting-component";
import { DeleteMonitorDialogComponent } from "@/routes/base-data/processes/qpm/monitors/[monitorId]/_components/delete-monitor-dialog";
import { monitorTypeToQuery } from "@/routes/base-data/processes/qpm/catalog/monitor-type-query";
import { useFeatureAuth } from "@/services/backend/auth/hooks/use-feature-auth";
import { MonitorAdditionalsEditor } from "@/routes/base-data/processes/qpm/monitors/[monitorId]/_components/monitor-additionals-editor";
import { MonitorDetailSection } from "@/routes/base-data/processes/qpm/monitors/[monitorId]/_components/monitor-detail-section";

export type PrevNextNav = {
  currentIndex: number;
  total: number;
  goPrev: () => void;
  goNext: () => void;
};

export function MonitorComponent({
  monitorId,
  prevNext,
}: {
  monitorId: string;
  prevNext?: PrevNextNav;
}) {
  const navigate = useNavigate();
  const { catalogId } = useParams();
  const canDeleteMonitor = useFeatureAuth("gsm.qpm.monitor.delete");

  const { data, error, isLoading } = useQpmMonitorGetQuery(
    { id: monitorId },
    {
      // When navigating between monitors, make sure we don't keep showing stale data.
      // RTK Query will cache by id, but this guarantees re-fetch on argument changes.
      refetchOnMountOrArgChange: true,
    },
  );

  const monitor: MonitorView | undefined = data;

  const { data: monitorList } = useQpmCatalogMonitorListQuery(
    catalogId && monitor
      ? {
          catalog: {
            active: true,
            values: [catalogId],
          },
          type: {
            active: true,
            values: [monitor.type],
          },
        }
      : {},
    {
      skip: !catalogId || !monitor,
    },
  );

  const sortedMonitorIds = useMemo(() => {
    if (prevNext) return [];
    if (!monitorList?.items) return [];
    return [...monitorList.items]
      .sort((a, b) =>
        (a.name ?? "").localeCompare(b.name ?? "", undefined, {
          sensitivity: "base",
        }),
      )
      .map((m) => m.id);
  }, [monitorList?.items, prevNext]);

  const currentIndex = useMemo(() => {
    if (prevNext) return prevNext.currentIndex;
    if (!sortedMonitorIds.length) return -1;
    return sortedMonitorIds.indexOf(monitorId);
  }, [sortedMonitorIds, monitorId, prevNext]);

  const goPrev = () => {
    if (prevNext) {
      prevNext.goPrev();
      return;
    }
    if (currentIndex <= 0) return;
    const nextId = sortedMonitorIds[currentIndex - 1];
    navigate(`../${nextId}`, { replace: true });
  };

  const goNext = () => {
    if (prevNext) {
      prevNext.goNext();
      return;
    }
    if (currentIndex < 0 || currentIndex >= sortedMonitorIds.length - 1) return;
    const nextId = sortedMonitorIds[currentIndex + 1];
    navigate(`../${nextId}`, { replace: true });
  };

  const hasPrev = currentIndex > 0;
  const hasNext = prevNext
    ? currentIndex >= 0 && currentIndex < prevNext.total - 1
    : currentIndex >= 0 && currentIndex < sortedMonitorIds.length - 1;

  if (isLoading) {
    return (
      <div className="flex w-full justify-center p-6">
        <Loader className="animate-spin" />
      </div>
    );
  }

  if (!data) {
    return null;
  }

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  const handleDeleteSuccess = () => {
    navigate(
      `/base-data/processes/qpm/monitors?type=${monitorTypeToQuery(data.type)}`,
      { replace: true },
    );
  };

  return (
    <>
      <CardHeader className="relative" key={monitorId}>
        <div className="flex items-center justify-between gap-2">
          <CardTitle>
            {MonitorTypeUtil.toLongString(data.type)} {t("Details")}
          </CardTitle>

          <div className="flex items-center gap-2">
            {canDeleteMonitor && (
              <DeleteMonitorDialogComponent
                monitor={data}
                onDeleteSuccess={handleDeleteSuccess}
              />
            )}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={goPrev}
                    disabled={!hasPrev}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t("Vorheriger")}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={goNext}
                    disabled={!hasNext}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t("Nächster")}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      </CardHeader>
      <CardContent key={`${monitorId}-content`}>
        <div className="space-y-6">
          <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_420px]">
            <div className="space-y-6">
              <MonitorDetailSection title={t("Stammdaten")}>
                <div className="grid gap-3 lg:grid-cols-2">
                  <MonitorNameComponent
                    key={`name-${monitorId}`}
                    monitorId={monitorId}
                    value={data.name}
                  />
                  <MonitorSupplierComponent
                    key={`supplier-${monitorId}`}
                    monitorId={monitorId}
                    value={data.supplier}
                  />
                  <MonitorIoTSpecComponent
                    key={`iot-${monitorId}`}
                    monitorId={monitorId}
                    iotSpec={data.capabilities?.iot}
                  />
                </div>
              </MonitorDetailSection>

              <MonitorDetailSection title={t("Einstellungen")}>
                <MonitorCapabilitiesComponent
                  key={`caps-${monitorId}`}
                  monitorId={monitorId}
                  monitorType={data.type}
                  caps={data.capabilities}
                />
              </MonitorDetailSection>
            </div>

            <MonitorDetailSection title={t("Bild")}>
              <MonitorImageComponent
                key={`img-${monitorId}`}
                monitor={data}
                className="w-full"
              />
            </MonitorDetailSection>
          </div>

          <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_420px]">
            <div className="space-y-6">
              <MonitorDetailSection title={t("Material und Kalkulation")}>
                <div className="space-y-3">
                  <MonitorVisitsComponent
                    key={`visits-${monitorId}`}
                    monitorId={monitorId}
                    value={data.recommendedVisits}
                  />
                  <MonitorTimesComponent
                    key={`times-${monitorId}`}
                    monitorId={monitorId}
                    installSeconds={data.times.installSeconds}
                    maintainSeconds={data.times.maintainSeconds}
                  />

                  <MonitorBasePriceComponent
                    key={`baseprice-${monitorId}`}
                    monitorId={monitorId}
                    price={data.basePrice?.euros}
                  />
                  <MonitorAdditionalsEditor
                    mode={{ kind: "base", monitorId }}
                    monitorName={data.name}
                    monitorType={data.type}
                    additionals={data.additionals ?? []}
                  />
                </div>
              </MonitorDetailSection>

              <MonitorDetailSection title={t("Beschreibung und Katalogtext")}>
                <MonitorDetailsComponent
                  key={`details-${monitorId}`}
                  monitorId={data.id}
                  monitor={data}
                />
              </MonitorDetailSection>
            </div>
            <div className="space-y-6">
              {MonitorTypeUtil.supportsRentingDefaults(data.type) ? (
                <MonitorDetailSection title={t("Konditionen")}>
                  <MonitorRentingComponent
                    key={`rent-${monitorId}`}
                    monitorId={monitorId}
                    renting={data.renting}
                  />
                </MonitorDetailSection>
              ) : null}
            </div>
          </div>

          <MonitorDetailSection title={t("Schädlingszuordnung")}>
            <div className="space-y-6">
              <MonitorPestsComponent
                key={`pests-${monitorId}`}
                monitorId={data.id}
                monitor={data}
              />

              {data.type === MonitorType.FlyExterminator ? (
                <MonitorConsumablesComponent
                  key={`consumables-${monitorId}`}
                  monitor={data}
                />
              ) : (
                <div />
              )}
            </div>
          </MonitorDetailSection>
        </div>
      </CardContent>
    </>
  );
}
